import * as THREE from 'three';

import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass';
import { GammaCorrectionShader } from 'three/examples/jsm/shaders/GammaCorrectionShader';
import { SMAAPass } from 'three/examples/jsm/postprocessing/SMAAPass';
import Experience from './Experience';
import { GUI } from 'dat.gui';


let instance = null;

export default class Renderer {
    constructor() {
        if (instance) {
            return instance;
        }

        instance = this;
        this.experience = new Experience();
        this.sizes = this.experience.sizes;
        this.scene = this.experience.scene;
        this.canvas = this.experience.canvas;
        this.camera = this.experience.camera;
        this.ENTIRE_SCENE = 0;
        this.BLOOM_SCENE = 1;

        this.bloomLayer = new THREE.Layers();
        this.bloomLayer.set(this.BLOOM_SCENE);
        this.darkMaterial = new THREE.MeshBasicMaterial({ color: "black" });

        this.materials = {};
        this.gui = new GUI();

        this.bloomParams = {
            exposure: 1, bloomStrength: 1.2, bloomThreshold: 0, bloomRadius: 0.11, scene: "Scene with Glow"
        }
        this.setInstance();
    }

    setInstance() {
        this.instance = new THREE.WebGLRenderer({
            canvas: this.canvas,
            powerPreference: 'high-performance',
            antialias: true
        });
        this.instance.shadowMap.enabled = true;
        this.instance.shadowMap.type = THREE.PCFSoftShadowMap;
        this.instance.toneMapping = THREE.ReinhardToneMapping;
        this.instance.physicallyCorrectLights = true;
        this.instance.outputEncoding = THREE.sRGBEncoding;
        this.instance.setSize(this.sizes.width, this.sizes.height);
        this.instance.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    }

    setGUIControls() {
        this.gui.add(this.bloomParams, 'exposure', 0.1, 2).onChange(value => {
            this.instance.toneMappingExposure = Math.pow(value, 4.0);
        });
        this.gui.add(this.bloomParams, 'bloomStrength', 0.0, 10.0).onChange(value => {
            this.bloomPass.strength = +value;
        });
        this.gui.add(this.bloomParams, 'bloomRadius', 0.0, 1.0).step(0.01).onChange(value => {
            this.bloomPass.radius = +value;
        });
    }

    setPostProcessing() {
        this.rendererPass = new RenderPass(this.scene, this.camera.instance);

        this.bloomPass = new UnrealBloomPass(new THREE.Vector2(window.innerWidth, window.innerHeight), 1.5, 0.4, 0.85);
        this.bloomPass.threshold = this.bloomParams.bloomThreshold;
        this.bloomPass.strength = this.bloomParams.bloomStrength;
        this.bloomPass.radius = this.bloomParams.bloomRadius;

        this.composer = new EffectComposer(this.instance);
        this.composer.renderToScreen = false;
        this.composer.addPass(this.rendererPass);
        this.composer.addPass(this.bloomPass);

        this.finalPass = new ShaderPass(new THREE.ShaderMaterial({
            uniforms: {
                baseTexture: { value: null }, bloomTexture: { value: this.composer.renderTarget2.texture }
            }, vertexShader: `
                varying vec2 vUv;

                void main() {

                    vUv = uv;

                    gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );

                }`, fragmentShader: `
                uniform sampler2D baseTexture;
                uniform sampler2D bloomTexture;

                varying vec2 vUv;

                void main() {

                    gl_FragColor = ( texture2D( baseTexture, vUv ) + vec4( 1.0 ) * texture2D( bloomTexture, vUv ) );

                }
                `, defines: {}
        }), "baseTexture");
        this.finalPass.needsSwap = true;

        this.finalComposer = new EffectComposer(this.instance);
        this.finalComposer.addPass(this.rendererPass);
        this.finalComposer.addPass(this.finalPass);

        // this.gammaCorrectionPass = new ShaderPass(GammaCorrectionShader);
        // this.finalComposer.addPass(this.gammaCorrectionPass);

        const smaaPass = new SMAAPass();
        this.finalComposer.addPass(smaaPass);

        // const renderTargetParameters = {
        //     minFilter: THREE.LinearFilter,
        //     magFilter: THREE.LinearFilter,
        //     stencilBuffer: false
        // };
        //
        // const savePass = new SavePass(
        //     new THREE.WebGLRenderTarget(
        //         window.innerWidth,
        //         window.innerHeight,
        //         renderTargetParameters
        //     )
        // );
        //
        // const blendPass = new ShaderPass(BlendShader, "tDiffuse1");
        // blendPass.uniforms["tDiffuse2"].value = savePass.renderTarget.texture;
        // blendPass.uniforms["mixRatio"].value = 0.2;
        //
        // const outputPass = new ShaderPass(CopyShader);
        // outputPass.renderToScreen = true;
        // this.composer.addPass(blendPass);
        // this.composer.addPass(savePass);
        // this.composer.addPass(outputPass);

        this.setGUIControls();
    }

    darkenNonBloomed(obj) {
        if (obj.isMesh && !this.bloomLayer.test(obj.layers)) {

            this.materials[obj.uuid] = obj.material;
            obj.material = this.darkMaterial;
        }
    }

    restoreMaterial(obj) {
        if (this.materials[obj.uuid]) {

            obj.material = this.materials[obj.uuid];
            delete this.materials[obj.uuid];
        }
    }

    resize() {
        this.instance.setSize(this.sizes.width, this.sizes.height);
        this.instance.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    }

    update() {
        // this.instance.render(this.scene, this.camera.instance);
        if (!this.rendererPass) return;

        this.scene.traverse(this.darkenNonBloomed.bind(this));
        this.composer.render();
        this.scene.traverse(this.restoreMaterial.bind(this));
        this.finalComposer.render();
    }
}